import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  CircularProgress,
  Button,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import { LocationOn, AccessTime, CalendarMonth, OpenInNew } from '@mui/icons-material';
import { supabase } from '../supabase';
import { Event } from '../types';
import { getAppStoreUrl } from '../appStoreUrl';

const GOOGLE_MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function EventDetailPage() {
  const { eventId, eventGroupId } = useParams<{ eventId?: string; eventGroupId?: string }>();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchEvent() {
      setLoading(true);
      setError(null);

      if (eventGroupId) {
        const { data, error: err } = await supabase
          .from('events')
          .select('*')
          .eq('event_group_id', eventGroupId)
          .eq('is_shareable', true)
          .order('starts_at', { ascending: true });

        if (err || !data || data.length === 0) {
          setError("This event group isn't shareable or does not exist.");
        } else {
          setEvents(data);
        }
      } else if (eventId) {
        const { data, error: err } = await supabase
          .from('events')
          .select('*')
          .eq('id', eventId)
          .single();

        if (err || !data) {
          setError("This event isn't shareable or does not exist.");
        } else if (!data.is_shareable) {
          setError("This event isn't shareable. Open it in the app to view details.");
        } else {
          setEvents([data]);
        }
      }

      setLoading(false);
    }

    fetchEvent();
  }, [eventId, eventGroupId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
        <Container maxWidth="xs" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 6, textAlign: 'center' }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: '#e3f2fd', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <LocationOn sx={{ fontSize: 32, color: '#1976d2' }} />
          </Box>
          <Typography variant="h5" fontWeight={600} gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Download Third Place to view events and meet up with friends.
          </Typography>
          <Button
            variant="contained"
            size="large"
            href={getAppStoreUrl()}
            sx={{ textTransform: 'none', borderRadius: 3, px: 4 }}
          >
            Get Third Place
          </Button>
          <Link to="/" style={{ marginTop: 16, color: '#1976d2', fontSize: 14 }}>
            Learn more
          </Link>
        </Container>
      </Box>
    );
  }

  const primaryEvent = events[0];
  const isGroup = events.length > 1;
  const hasLocation = primaryEvent.latitude && primaryEvent.longitude;
  const mapsUrl = hasLocation
    ? `https://maps.google.com/?q=${primaryEvent.latitude},${primaryEvent.longitude}`
    : undefined;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#f8f9fa' }}>
      {/* Hero header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 50%, #0d47a1 100%)',
          px: 3,
          pt: { xs: 5, sm: 6 },
          pb: { xs: 4, sm: 5 },
        }}
      >
        <Container maxWidth="sm" disableGutters>
          {isGroup && (
            <Chip
              label={`${events.length} events`}
              size="small"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 500, mb: 1 }}
            />
          )}
          <Typography variant="h4" fontWeight={700} sx={{ color: 'white' }}>
            {primaryEvent.name}
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ flex: 1, mt: -2, position: 'relative', zIndex: 1, pb: 4 }}>
        {/* Main details card */}
        <Box sx={{ bgcolor: 'white', borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {events.map((evt, index) => (
            <Box key={evt.id}>
              {isGroup && index > 0 && <Divider />}
              <Box sx={{ p: 3 }}>
                {isGroup && (
                  <Typography variant="overline" color="primary" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
                    {evt.event_type === 'ping' ? 'Ping' : 'Event'} {index + 1}
                    {index > 0 && ` \u2014 ${evt.name}`}
                  </Typography>
                )}

                {evt.description && (
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
                    {evt.description}
                  </Typography>
                )}

                <Stack spacing={2}>
                  <DetailRow
                    icon={<CalendarMonth sx={{ fontSize: 20, color: '#1976d2' }} />}
                    label="Date"
                    text={formatDate(evt.starts_at)}
                  />
                  {evt.expires_at && (
                    <DetailRow
                      icon={<AccessTime sx={{ fontSize: 20, color: '#1976d2' }} />}
                      label="Duration"
                      text={formatDuration(evt.starts_at, evt.expires_at)}
                    />
                  )}
                  {evt.location_name && (
                    <DetailRow
                      icon={<LocationOn sx={{ fontSize: 20, color: '#1976d2' }} />}
                      label="Location"
                      text={evt.location_name}
                      href={evt.latitude && evt.longitude
                        ? `https://maps.google.com/?q=${evt.latitude},${evt.longitude}`
                        : undefined
                      }
                    />
                  )}
                </Stack>
              </Box>
            </Box>
          ))}

          {/* Map */}
          {hasLocation && GOOGLE_MAPS_KEY && (
            <>
              <Divider />
              <Box
                component="a"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ display: 'block', lineHeight: 0 }}
              >
                <Box
                  component="img"
                  src={staticMapUrl(events, GOOGLE_MAPS_KEY)}
                  alt="Event location"
                  sx={{ width: '100%', height: { xs: 200, sm: 240 }, objectFit: 'cover', display: 'block' }}
                />
              </Box>
              <Box
                component="a"
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  py: 1.5,
                  textDecoration: 'none',
                  color: '#1976d2',
                  fontWeight: 500,
                  fontSize: 14,
                  '&:hover': { bgcolor: '#f5f8ff' },
                  transition: 'background-color 0.15s',
                }}
              >
                Open in Google Maps
                <OpenInNew sx={{ fontSize: 16 }} />
              </Box>
            </>
          )}
        </Box>

        {/* CTA */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 4,
            p: 3,
            bgcolor: 'white',
            borderRadius: 3,
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            RSVP, chat with attendees, and more
          </Typography>
          <Button
            variant="contained"
            size="large"
            href={getAppStoreUrl()}
            fullWidth
            sx={{
              textTransform: 'none',
              borderRadius: 2.5,
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
            }}
          >
            Get Third Place
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Box component="footer" sx={{ textAlign: 'center', py: 2 }}>
        <Link to="/" style={{ color: '#999', fontSize: 13, textDecoration: 'none' }}>
          mythird.place
        </Link>
      </Box>
    </Box>
  );
}

function DetailRow({ icon, label, text, href }: { icon: React.ReactNode; label: string; text: string; href?: string }) {
  const content = (
    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
      <Box sx={{ mt: 0.25 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.2, mb: 0.25 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {text}
        </Typography>
      </Box>
    </Box>
  );
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
        {content}
      </a>
    );
  }
  return content;
}

function staticMapUrl(events: Event[], apiKey: string): string {
  const markers = events
    .filter((e) => e.latitude && e.longitude)
    .map((e) => `${e.latitude},${e.longitude}`)
    .join('|');
  return `https://maps.googleapis.com/maps/api/staticmap?size=800x400&scale=2&maptype=roadmap&style=feature:poi|visibility:off&markers=color:0x1976d2|${markers}&key=${apiKey}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function formatDuration(start: string, end: string): string {
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const hours = Math.floor(ms / 3_600_000);
  const minutes = Math.floor((ms % 3_600_000) / 60_000);
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

export default EventDetailPage;
